import React, { useState, useEffect, useRef } from "react";
import DashboardContainerHeader from "../../../Dashboard_en/Components/DashboardContainerHeader/DashboardContainerHeader";
import DashboardContainer from "../../../Dashboard_en/DashboardContainer";
import TextInput from "../../../Dashboard_en/Components/Inputs/TextInput/TextInput";
import TextAreaInput from "../../../Dashboard_en/Components/Inputs/TextAreaInput/TextAreaInput";
import ApiName from "../../../ApiName";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";

// Custom Tailwind File Upload Component
const TailwindFileUpload = ({ onFilesSelected, disabled }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      onFilesSelected(e.dataTransfer.files);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (disabled) return;
    
    if (e.target.files && e.target.files.length > 0) {
      onFilesSelected(e.target.files);
    }
  };

  return (
    <div 
      className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors ${disabled ? 'opacity-50 cursor-not-allowed border-gray-300 bg-gray-50 dark:bg-gray-800' : isDragging ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900' : 'border-gray-300 hover:border-indigo-400 bg-white dark:bg-gray-800'}`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => !disabled && fileInputRef.current?.click()}
    >
      <input 
        type="file" 
        multiple 
        accept="image/*" 
        className="hidden" 
        ref={fileInputRef} 
        onChange={handleChange}
        disabled={disabled}
      />
      <div className="flex flex-col items-center justify-center space-y-3 cursor-pointer">
        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
        <p className="text-gray-600 dark:text-gray-300"><span className="font-semibold text-indigo-600 dark:text-indigo-400">Click to upload</span> or drag and drop</p>
      </div>
    </div>
  );
};

const AdminAddPaint = () => {
  const [paintID, setPaintID] = useState(null);
  const [progressBarValue, setProgressBar] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [imageAvailable, setImageAvailablity] = useState(true);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [subCategoryId, setSubCategoryId] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.get(ApiName + "/Category");
        setCategories(response.data);
      } catch (error) {
        console.error("Error fetching categories", error);
      }
    };
    fetchCategories();
  }, []);

  const createPaintHandler = async () => {
    const data = {
      paintName: document.getElementById("paintName").value,
      paintDesc: document.getElementById("paintDesc").value,
      paintSellURL: document.getElementById("paintSellURL").value,
      paintSmallDesc: document.getElementById("paintSmallDesc").value,
      paintSpecs: document.getElementById("paintSpecs").value,
      categoryId: categoryId ? parseInt(categoryId) : null,
      subCategoryId: subCategoryId ? parseInt(subCategoryId) : null
    };

    try {
      const response = await axios.post(ApiName + "/Paint", data, {
        headers: {
          Authorization: localStorage.authToken,
          "Content-Type": "application/json",
        }
      });
      toast.success("Paint created successfully!");
      setPaintID(response.data.id);
      setImageAvailablity(false); // Enable the image upload section
    } catch (error) {
      toast.warning("There was some kind of problem in creating process.");
    }
  };

  const uploadImageHandler = async (files) => {
    if (!paintID) return;

    var form = new FormData();
    for (var i = 0; i < files.length; i++) {
      if (files[i].size > 4194304) {
        toast.error(`File ${files[i].name} exceeds the 4 MB limit.`);
        return;
      }
      form.append("files", files[i]);
    }

    setIsUploading(true);
    setImageAvailablity(true); // Disable while uploading

    try {
      await axios.post(ApiName + "/Paint/addimage/" + paintID, form, {
        headers: {
          Authorization: localStorage.authToken,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setProgressBar(percentCompleted);
        }
      });
      setIsUploading(false);
      setImageAvailablity(false);
      setProgressBar(0);
      toast.success("Images added successfully");
      navigate("/admin/paints");
    } catch (error) {
      setIsUploading(false);
      setImageAvailablity(false);
      setProgressBar(0);
      toast.warning("There was a problem uploading the images!");
    }
  };

  const selectedCategoryObj = categories.find(c => c.id.toString() === categoryId);

  return (
    <motion.div
      initial={{ opacity: 0, transition: { duration: 0.1 } }}
      animate={{ opacity: 1, transition: { duration: 0.5 } }}
      exit={{ opacity: 0, transition: { duration: 0.1 } }}
      className="grid grid-cols-1 gap-3 px-5 lg:grid-cols-2"
    >
      <div className="w-full">
        <DashboardContainer>
          <div className="w-full">
            <DashboardContainerHeader
              classNames={!imageAvailable ? "text-zinc-200" : "text-zinc-900"}
              text="Add Paint"
            />
            
            {/* Tailwind Success Message Replacement */}
            {!imageAvailable && (
              <div className="mx-5 mt-4 p-4 mb-4 text-sm text-green-800 rounded-lg bg-green-50 dark:bg-gray-800 dark:text-green-400" role="alert">
                <span className="font-medium">Success!</span> Paint Created Successfully. Now Add the Images.
              </div>
            )}

            <div className="px-5 py-5">
              <TextInput
                disabled={!imageAvailable}
                label="Name"
                id="paintName"
              />
              <TextInput
                disabled={!imageAvailable}
                label="Small Description"
                id="paintSmallDesc"
              />
              <TextInput
                disabled={!imageAvailable}
                label="Sale URL"
                id="paintSellURL"
              />
              <TextAreaInput
                disabled={!imageAvailable}
                label="Description"
                id="paintDesc"
              />
              <TextAreaInput
                disabled={!imageAvailable}
                label="Specifications"
                id="paintSpecs"
              />
              
              <div className="flex flex-col mb-4">
                <label className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">Category</label>
                <select
                  value={categoryId}
                  onChange={(e) => {
                    setCategoryId(e.target.value);
                    setSubCategoryId("");
                  }}
                  disabled={!imageAvailable}
                  className="p-2 border border-gray-300 rounded dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Select a Category</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              {categoryId && selectedCategoryObj && selectedCategoryObj.subCategories && selectedCategoryObj.subCategories.length > 0 && (
                <div className="flex flex-col mb-4">
                  <label className="mb-2 text-sm font-semibold text-gray-700 dark:text-gray-300">SubCategory</label>
                  <select
                    value={subCategoryId}
                    onChange={(e) => setSubCategoryId(e.target.value)}
                    disabled={!imageAvailable}
                    className="p-2 border border-gray-300 rounded dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Select a SubCategory</option>
                    {selectedCategoryObj.subCategories.map((sub) => (
                      <option key={sub.id} value={sub.id}>{sub.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="py-3"></div>
              
              {/* Tailwind Button Replacement */}
              <button
                onClick={createPaintHandler}
                disabled={!imageAvailable}
                className="px-6 py-2 mt-4 font-medium text-white transition-colors bg-zinc-900 rounded-lg hover:bg-zinc-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                Submit
              </button>
            </div>
          </div>
        </DashboardContainer>
      </div>
      <div className="w-full">
        <DashboardContainer>
          <div>
            <DashboardContainerHeader
              classNames={imageAvailable ? "text-zinc-400" : "text-zinc-900"}
              text="Add Paint Image"
            />
            <div className="px-5 py-10 pt-10">
              
              {/* Tailwind Progress Bar Replacement */}
              {isUploading && (
                <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700 mb-6">
                  <div className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300 ease-in-out" style={{ width: `${progressBarValue}%` }}></div>
                </div>
              )}

              <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
                📎 Max <strong>4 MB</strong> per image — all formats auto-converted to <strong>WebP</strong>
              </p>
              
              {/* Tailwind FileUpload Replacement */}
              <TailwindFileUpload 
                disabled={imageAvailable || isUploading}
                onFilesSelected={uploadImageHandler}
              />
            </div>
          </div>
        </DashboardContainer>
      </div>
    </motion.div>
  );
};

export default AdminAddPaint;

